import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import EventsList from './elements/EventsList'
import FilterTab from './elements/FilterTab'
import SimilarEventsTab from './elements/SimilarEventsTab'
import Tabs from 'components/Shared/Tabs'
import { SecondaryButton } from 'components/Shared/Button'
import EditEventSeriesProvider, { useEditEventSeriesContext } from './EditEventSeriesProvider'
import PortalModal from 'components/Shared/PortalModal'
import ConfirmationModal from 'components/Shared/ConfirmationModal'
import { useToast } from 'components/Shared/AppToast/ToastProvider'


function EditEventSeriesContent({ isOpen, onClickOutside, eventId }) {
  const { t } = useTranslation()
  const {
    includedEvents,
    saveChanges,
    loading,
    hasChanges,
    resetState,
    reloadSeriesData,
  } = useEditEventSeriesContext()
  const { addNotification } = useToast()
  const [currentTab, setCurrentTab] = useState(0)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('body-no-scroll')
    } else {
      document.body.classList.remove('body-no-scroll')
    }

    return () => {
      document.body.classList.remove('body-no-scroll')
    }
  }, [isOpen])

  const tabsData = [
    {
      title: t('Event.editEventSeriesModal.similarEvents'),
      component: <SimilarEventsTab />,
    },
    {
      title: t('common.search'),
      component: <FilterTab />,
    },
  ]

  const handleTabChange = (tab) => {
    setCurrentTab(tab)
  }

  const handleSave = async () => {
    try {
      await saveChanges()
      await reloadSeriesData()
      addNotification({
        message: t('Event.editEventSeriesModal.eventSeriesSavedTitle'),
        description: t('Event.editEventSeriesModal.eventSeriesSavedDescription'),
        variant: 'success',
      })
    } catch (error) {
      addNotification({
        message: t('common.genericError'),
        description: t('common.errors.inServerResponse'),
        variant: 'error',
      })
    }
  }

  const handleClose = () => {
    if (hasChanges) {
      setIsConfirmationModalOpen(true)
    } else {
      onClickOutside()
      resetState()
    }
  }

  const handleConfirmClose = () => {
    setIsConfirmationModalOpen(false)
    onClickOutside()
    resetState()
  }

  return (
    <PortalModal
      isOpen={isOpen}
      onClickOutside={handleClose}
      title={t('Event.editEventSeriesModal.title')}
      wrapperClassName="edit-event-series-modal-wrapper"
    >
      <div className='edit-event-series'>
        <div className='section-wrapper'>
          <p className='section-title'>{t('Event.editEventSeriesModal.includedEvents')}</p>

          <EventsList events={includedEvents.filter((event) => event.id !== eventId)} isIncluded={true} emptyMessage={t('Event.editEventSeriesModal.notPartOfSeries')} />
        </div>

        <Tabs tabsData={tabsData} currentTab={currentTab} handleTabChange={handleTabChange} classNames='p-0 edit-event-series-tabs' />
      
        <div className='footer'>
          <SecondaryButton 
            text={t('Event.editEventSeriesModal.btnSave')} 
            onClick={handleSave} 
            classNames='footer_btn' 
            fullWidth={false}
            disabled={loading || !hasChanges}
          />
        </div>

        <PortalModal isOpen={isConfirmationModalOpen} onClickOutside={() => setIsConfirmationModalOpen(false)}>
          <ConfirmationModal
            title={t('Event.editEventSeriesModal.unsavedChangesTitle')}
            description={t('Event.editEventSeriesModal.unsavedChangesDescription')}
            onCancel={() => setIsConfirmationModalOpen(false)}
            onConfirm={handleConfirmClose}
          />
        </PortalModal>
      </div>
    </PortalModal>
  )
}

export default function EditEventSeries(props) {
  const { onClose, eventId, isOpen, onClickOutside } = props

  return (
    <EditEventSeriesProvider eventId={eventId} onClose={onClose}>
      <EditEventSeriesContent isOpen={isOpen} onClickOutside={onClickOutside} eventId={eventId}/>
    </EditEventSeriesProvider>
  )
}

EditEventSeries.propTypes = {
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}